'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import Image from 'next/image';
import { GraduationCap, Users, Award, BookOpen } from 'lucide-react';

export type AuthRole = 'admin' | 'staff' | 'student';
export type AuthMode = 'login' | 'register';

export default function AuthScreen() {
  const [role, setRole] = useState<AuthRole>('student');
  const [mode, setMode] = useState<AuthMode>('login');

  const roles: { key: AuthRole; label: string; icon: string }[] = [
    { key: 'admin', label: 'Admin', icon: '⚙' },
    { key: 'staff', label: 'Staff', icon: '👨‍🏫' },
    { key: 'student', label: 'Student', icon: '🎓' },
  ];

  const stats = [
    { value: '15,000+', label: 'Students Trained', icon: <Users size={20} /> },
    { value: '30 Yrs', label: 'Of Excellence', icon: <Award size={20} /> },
    { value: '12+', label: 'Courses Offered', icon: <BookOpen size={20} /> },
  ];

  return (
    <div className="min-h-screen flex items-stretch" style={{ background: '#F8FAFC' }}>
      {/* Left Panel — Professional Brand Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden flex-col items-center justify-center p-12"
        style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #162D4A 60%, #0F1E33 100%)' }}
      >
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #F59E0B, #2563EB, #F59E0B)' }} />

        {/* Content */}
        <div className="relative z-10 text-center max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="relative w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg"
              style={{ border: '2px solid rgba(255,255,255,0.2)' }}>
              <Image
                src="/assets/images/logo1-1786300024701.jpg"
                alt="YASH Computers KEONICS Logo"
                fill
                className="object-contain p-1"
              />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold text-white leading-tight">YASH Computers</div>
              <div className="text-sm font-medium" style={{ color: '#93C5FD' }}>KEONICS Franchisee</div>
            </div>
          </div>

          {/* Headline */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-semibold"
              style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', color: '#FCD34D' }}>
              <GraduationCap size={15} />
              Empowering Careers Since 1996
            </div>
            <h1 className="text-3xl font-bold text-white leading-snug mb-3">
              Premier Computer<br />Education Center
            </h1>
            <p className="text-base leading-relaxed" style={{ color: '#93C5FD' }}>
              Government-recognized KEONICS franchisee offering industry-relevant computer courses with placement support.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-xl p-4 text-center"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex justify-center mb-2" style={{ color: '#60A5FA' }}>{stat.icon}</div>
                <div className="text-xl font-bold text-white">{stat.value}</div>
                <div className="text-xs mt-0.5" style={{ color: '#93C5FD' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Course badges */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {['ADCA', 'DIFA', 'DTP', 'Tally Prime', 'Office Mgmt', 'Web Dev'].map((course) => (
              <span key={course} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.3)', color: '#93C5FD' }}>
                {course}
              </span>
            ))}
          </div>

          {/* Contact */}
          <div className="flex flex-wrap justify-center gap-4 text-xs" style={{ color: '#64748B' }}>
            <span style={{ color: '#93C5FD' }}>🌐 www.yashcomputers.com</span>
            <span style={{ color: '#93C5FD' }}>📞 9900658291 / 08443358053</span>
          </div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Logo - Mobile only */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="relative flex-shrink-0 w-10 h-10">
              <Image
                src="/assets/images/IMG-20250501-WA0005-1786297878800.jpg"
                alt="YASH Computers Logo"
                fill
                className="object-contain rounded-xl"
                style={{ border: '1px solid var(--border)' }}
              />
            </div>
            <div>
              <div className="font-bold text-foreground text-lg leading-tight">YASH Computers</div>
              <div className="text-xs text-muted-foreground">Education Center Portal</div>
            </div>
          </div>

          {/* Desktop heading */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-2xl font-bold" style={{ color: '#1E3A5F' }}>Welcome Back</h2>
            <p className="text-sm text-muted-foreground mt-1">Sign in to your YASH Computers portal</p>
          </div>

          {/* Role Tabs */}
          <div className="flex rounded-xl p-1 mb-6" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
            {roles.map((r) => (
              <button
                key={`role-tab-${r.key}`}
                onClick={() => { setRole(r.key); setMode('login'); }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1.5 ${
                  role === r.key
                    ? 'text-white shadow-md'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                style={role === r.key ? { background: 'linear-gradient(135deg, #1E3A5F, #2563EB)' } : {}}
              >
                <span>{r.icon}</span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>

          {/* Mode Toggle (only for student) */}
          {role === 'student' && (
            <div className="flex rounded-lg overflow-hidden mb-6" style={{ border: '1px solid var(--border)' }}>
              {(['login', 'register'] as AuthMode[]).map((m) => (
                <button
                  key={`mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    mode === m
                      ? 'text-white' :'text-muted-foreground hover:text-foreground'
                  }`}
                  style={mode === m ? { background: '#1E3A5F' } : {}}
                >
                  {m === 'login' ? 'Sign In' : 'Register'}
                </button>
              ))}
            </div>
          )}

          {/* Form Card */}
          <div className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(30,58,95,0.08)' }}>
            <div className="mb-5">
              <h2 className="text-xl font-bold" style={{ color: '#1E3A5F' }}>
                {mode === 'login' ? `Welcome back` : 'Create Account'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'login'
                  ? `Sign in to your ${role} portal`
                  : 'Register as a new student — pending admin approval'}
              </p>
            </div>

            {mode === 'login' ? (
              <LoginForm role={role} />
            ) : (
              <RegisterForm />
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            © 2026 YASH Computers. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}